import { CommonModule } from "@angular/common"
import { Component, OnDestroy, OnInit } from "@angular/core"
import { Router } from "@angular/router"
import { ToastService } from "src/app/services"
import { BehaviorSubject, Observable, Subscription } from "rxjs"
import { Application, ApplicationState, FileContent, FileContentFactory } from "src/app/models"
import { ContextService, LoadingService } from "src/app/services"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { FileInputComponent } from "../../components/file-input/file-input.component"
type ContractInputs = {
    rentalAgreement: FileContent
    subrentalAgreement: FileContent
}

@Component({
    selector: "valben-kng-upload-contracts",
    templateUrl: "./kng-upload-contracts.page.html",
    styles: [`
        :host {
            width: 100%;
            display: flex;
            flex-direction: column;
        }
    `],
    standalone: true,
    imports: [
        MatProgressSpinnerModule,
        CommonModule,
        FileInputComponent,
    ],
})
export class KngUploadContractsPage implements OnInit, OnDestroy {
    loading$ = this._loadingService.kngLoading()
    itemsSubscription: Subscription | undefined = undefined

    applications: Application[] = []
    selectedApplication: Application | undefined = undefined
    uploadForm: { id: string }[] = []
    contractItemObservable: () => Observable<ContractInputs> | undefined = () => undefined
    updateRental: (file: FileContent) => void = () => {}
    updateSubrental: (file: FileContent) => void = () => {}
    private _formCounter = 0
    private _contractInputs = new Map<string, BehaviorSubject<ContractInputs>>()

    constructor(
        private _contextService: ContextService,
        private _loadingService: LoadingService,
        private _messageService: ToastService,
        private _router: Router,
    ) {}

    ngOnInit(): void {
        this.itemsSubscription = this._contextService.getKngSubject()?.subscribe(applications => {
            this.applications = applications.filter(app => app.state === ApplicationState.ContractGenerated)

            this.applications.forEach(app => {
                if (!this._contractInputs.has(app.masterId)) {
                    this._contractInputs.set(app.masterId, new BehaviorSubject<ContractInputs>({
                        rentalAgreement: app.rentalAgreement
                            ? FileContentFactory.fromUrl(app.rentalAgreement)
                            : FileContentFactory.create(),
                        subrentalAgreement: app.subrentalAgreement
                            ? FileContentFactory.fromUrl(app.subrentalAgreement)
                            : FileContentFactory.create(),
                    }))
                }
            })

            Array.from(this._contractInputs.keys())
                .filter(masterId => !this.applications.some(app => app.masterId === masterId))
                .forEach(masterId => this._contractInputs.delete(masterId))

            if (this.selectedApplication && !this.applications.some(app => app.masterId === this.selectedApplication!.masterId)) {
                this.selectedApplication = undefined
                this.uploadForm = []
            }
        })
    }

    ngOnDestroy(): void {
        this.itemsSubscription?.unsubscribe()
    }

    back(): void {
        this._router.navigate(["/kng-approved"])
    }

    selectApplication(application: Application): void {
        if (this.selectedApplication?.masterId === application.masterId) {
            return
        }

        if (this.selectedApplication) {
            const previous = this._findApplication(this.selectedApplication.masterId)
            if (previous) {
                this._resetContractInputs(previous)
            }
        }

        this._resetContractInputs(application)
        this.selectedApplication = application
        this.uploadForm = [{ id: `${application.masterId}-${++this._formCounter}` }]
        this.contractItemObservable = this.itemObservable(application.masterId)
        this.updateRental = file => this.updateRentalAgreement(application.masterId, file)
        this.updateSubrental = file => this.updateSubrentalAgreement(application.masterId, file)
    }

    itemObservable(masterId: string): () => Observable<ContractInputs> | undefined {
        return () => this._contractInputs.get(masterId)?.asObservable()
    }

    updateRentalAgreement(masterId: string, file: FileContent): void {
        this._update(masterId, { rentalAgreement: file })
    }

    updateSubrentalAgreement(masterId: string, file: FileContent): void {
        this._update(masterId, { subrentalAgreement: file })
    }

    saveContracts(application: Application): void {
        const value = this._contractInputs.get(application.masterId)?.value
        if (!value) return

        if (!this._hasFile(value.rentalAgreement) || !this._hasFile(value.subrentalAgreement)) {
            this._messageService.add({
                severity: "error",
                summary: "Greška",
                detail: "Potrebno je priložiti oba potpisana ugovora.",
                life: 10000,
            })
            return
        }

        this._contextService.adminModifyContracts(application.masterId, value).subscribe({
            next: () => {
                this._contractInputs.delete(application.masterId)

                if (this.selectedApplication?.masterId === application.masterId) {
                    this.selectedApplication = undefined
                    this.uploadForm = []
                }

                this._messageService.add({
                    severity: "success",
                    summary: "Uspjeh",
                    detail: `${application.name} ${application.surname}: ugovori su spremljeni.`,
                    life: 10000,
                })
            },
            error: err => {
                this._messageService.add({
                    severity: "error",
                    summary: "Greška",
                    detail: err?.error?.message ?? err?.error ?? "Došlo je do greške prilikom spremanja ugovora",
                    life: 10000,
                })
            },
        })
    }

    private _findApplication(masterId: string): Application | undefined {
        return this.applications.find(app => app.masterId === masterId)
    }

    private _resetContractInputs(application: Application): void {
        this._contractInputs.set(application.masterId, new BehaviorSubject<ContractInputs>({
            rentalAgreement: application.rentalAgreement
                ? FileContentFactory.fromUrl(application.rentalAgreement)
                : FileContentFactory.create(),
            subrentalAgreement: application.subrentalAgreement
                ? FileContentFactory.fromUrl(application.subrentalAgreement)
                : FileContentFactory.create(),
        }))
    }

    private _update(masterId: string, partial: Partial<ContractInputs>): void {
        const current = this._contractInputs.get(masterId)?.value
        if (!current) return
        this._contractInputs.get(masterId)?.next({
            ...current,
            ...partial,
        })
    }

    private _hasFile(file: FileContent): boolean {
        return !!(file?.base64 || file?.url)
    }
}
